import { Table } from 'legacy/lut/library/list'
import React, { Dispatch, SetStateAction, useCallback, useMemo, useState } from 'react'
import { useSelector } from 'react-redux'

import { Translation, useSafetyTranslation } from 'cng-common/features/translations'
import { TranslationMode } from 'cng-common/features/translations/configs'
import { selectTranslationMode } from 'cng-common/features/translations/selectors'
import { Button } from 'core-components/library/button'
import { IRowAction } from 'core-components/library/list/types'
import { Static } from 'core-components/types'
import { Crud } from 'core/types'
import { TopPartnerEditor } from 'partner360/features/topPartnersManagement/components/topPartnerEditor/TopPartnerEditor'
import { INCLUDE_MARKET_FLAGS } from 'partner360/features/topPartnersManagement/configs'
import { getMarketCoverage } from 'partner360/features/topPartnersManagement/helpers/getMarketCoverage'
import { isActiveFlagRequest } from 'partner360/features/topPartnersManagement/helpers/isActiveFlagRequest'
import { sumMarketCoverage } from 'partner360/features/topPartnersManagement/helpers/sumMarketCoverage'
import { useFlagRequestActions } from 'partner360/features/topPartnersManagement/hooks/useFlagRequestActions'
import {
  ICoverageImpact,
  IPreparedTopPartner,
} from 'partner360/features/topPartnersManagement/models'
import { TRANSLATION_KEYS } from 'partner360/features/topPartnersManagement/translations'
import { IP360PartnerFlags } from 'partner360/models/p360PartnerFlags/IP360PartnerFlags'
import { IPartnerWithFlagRequests } from 'partner360/models/partnerFlagRequests/IPartnerWithFlagRequests'

import { RemoveFlagConfirmationDialog } from './components/removeFlagConfirmationDialog/RemoveFlagConfirmationDialog'
import { getColumns } from './helpers/getColumns'

import styles from './TopPartnersManagementTable.module.scss'

interface ITopPartnersManagementTableProps {
  partnersForTable: IPreparedTopPartner[]
  allPartners: IPartnerWithFlagRequests[]
  fYear?: number
  isPbm: boolean
  partnerFlags: IP360PartnerFlags[]
  topFlags: IP360PartnerFlags[]
  setPartners: Dispatch<SetStateAction<IPartnerWithFlagRequests[]>>
  approvers: string[]
}

export const TopPartnersManagementTable = ({
  partnersForTable,
  fYear,
  topFlags,
  partnerFlags,
  allPartners,
  setPartners,
  approvers,
}: ITopPartnersManagementTableProps) => {
  const [selectedItem, setSelectedItem] = useState<IPartnerWithFlagRequests>(
    {} as IPartnerWithFlagRequests
  )

  const [showModal, setShowModal] = useState(false)
  const [deleteTarget, setDeleteTarget] = useState<IPreparedTopPartner | null>(null)

  const t = useSafetyTranslation()
  const translationMode = useSelector(selectTranslationMode)
  const { isLoading, createRequest, updateRequestStatus } = useFlagRequestActions({
    fYear,
    approvers,
    setPartners,
  })

  const removalFlag = topFlags[0]

  const closeDeleteConfirmation = useCallback(() => {
    if (!isLoading) setDeleteTarget(null)
  }, [isLoading])

  const handleConfirmRemoveFlag = useCallback(async () => {
    if (!deleteTarget || isLoading) return

    if (isActiveFlagRequest(deleteTarget.RequestStatus)) {
      if (deleteTarget.RequestId == null) {
        return
      }
      const res = await updateRequestStatus(deleteTarget.RequestId, 'Deleted')
      if (res) {
        setDeleteTarget(null)
      }
    } else {
      if (!removalFlag?.FlagKey) {
        return
      }
      const res = await createRequest(deleteTarget.PartnerId, { [removalFlag.FlagKey]: 0 })
      if (res) {
        setDeleteTarget(null)
      }
    }
  }, [deleteTarget, isLoading, removalFlag, updateRequestStatus, createRequest])

  const coverageImpact = useMemo<ICoverageImpact | null>(() => {
    if (
      !deleteTarget ||
      !removalFlag?.BitValue ||
      isActiveFlagRequest(deleteTarget.RequestStatus)
    ) {
      return null
    }

    const marketPartners = allPartners.filter(
      (partner) => partner.GeoMarket === deleteTarget.GeoMarket
    )

    const current = sumMarketCoverage(marketPartners, removalFlag) ?? 0
    const ownCoverage = getMarketCoverage(deleteTarget.Coverage, removalFlag) ?? 0

    return { current, next: Math.max(current - ownCoverage, 0) }
  }, [deleteTarget, removalFlag, allPartners])

  const rowActions = useMemo<IRowAction<IPreparedTopPartner>[]>(() => {
    if (INCLUDE_MARKET_FLAGS) {
      return [
        {
          id: 'edit',
          title: 'edit',
          icon: 'kbase kbase-edit kbase-sm',
          action: (item) => {
            setSelectedItem(item)
            setShowModal(true)
          },
        },
      ]
    }

    return [
      {
        id: 'removeRequest',
        title: t(TRANSLATION_KEYS.topPartners.removeFlag.removeRequestTooltip),
        icon: 'kbase kbase-delete kbase-sm',
        isVisible: (item) => isActiveFlagRequest(item.RequestStatus),
        action: (item) => setDeleteTarget(item),
      },
      {
        id: 'createRemoveRequest',
        title: t(TRANSLATION_KEYS.topPartners.removeFlag.createRequestTooltip),
        icon: 'kbase kbase-delete kbase-sm',
        isVisible: (item) => !isActiveFlagRequest(item.RequestStatus),
        action: (item) => setDeleteTarget(item),
      },
    ]
  }, [t])

  const partnersNotInTable = useMemo(() => {
    const partnerIdsInTable = new Set(partnersForTable.map((item) => item.PartnerId))

    return allPartners.filter(
      (partner) =>
        !partnerIdsInTable.has(partner.PartnerId) && !isActiveFlagRequest(partner.RequestStatus)
    )
  }, [allPartners, partnersForTable])

  const source = new Crud({
    fetcher: new Static(partnersForTable),
    keyField: 'PartnerId',
    defaultPaging: { page: 0, pageSize: 100 },
    defaultSort: { field: 'RequestCreatedDate', dir: 'desc' },
  })

  const columns = useMemo(() => {
    return getColumns({ currentYear: fYear, topFlags, partnerFlags })
  }, [fYear, topFlags, partnerFlags])

  return (
    <div className={styles.root}>
      <div className={styles.actions}>
        <Button onClick={() => setShowModal(true)}>
          <Translation text={TRANSLATION_KEYS.topPartners.addNewTopPartner.addPartner} />
        </Button>
      </div>

      {translationMode === TranslationMode.Edit && (
        <div>
          <Translation text={TRANSLATION_KEYS.topPartners.removeFlag.removeRequestTooltip} />
          <Translation text={TRANSLATION_KEYS.topPartners.removeFlag.createRequestTooltip} />
        </div>
      )}

      <Table
        header
        pagination
        hasExport
        source={source}
        columns={columns}
        rowActionsVisible={'visible'}
        rowActions={rowActions}
        classes={{ root: styles.hideColumnsFilter }}
      />

      <RemoveFlagConfirmationDialog
        item={deleteTarget}
        partnerFlags={partnerFlags}
        flagTitle={removalFlag?.Title}
        coverageImpact={coverageImpact}
        isVisible={Boolean(deleteTarget)}
        hasActiveRequest={isActiveFlagRequest(deleteTarget?.RequestStatus)}
        isApplyDisabled={
          isLoading ||
          (isActiveFlagRequest(deleteTarget?.RequestStatus)
            ? deleteTarget?.RequestId == null
            : !removalFlag?.FlagKey)
        }
        onConfirm={handleConfirmRemoveFlag}
        onReject={closeDeleteConfirmation}
      />

      <TopPartnerEditor
        partnerFlags={partnerFlags}
        show={showModal}
        partnerName={selectedItem.PartnerName}
        partnerId={selectedItem.PartnerId}
        topFlags={topFlags}
        bitClass={selectedItem.BitClass}
        fYear={fYear}
        item={selectedItem}
        partnersNotInTable={partnersNotInTable}
        allPartners={allPartners}
        setPartners={setPartners}
        approvers={approvers}
        onClose={() => {
          setSelectedItem({} as IPartnerWithFlagRequests)
          setShowModal(false)
        }}
      />
    </div>
  )
}
